import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  SxProps,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BottomSheet } from '../../../components/_mui';
import {
  DESELECT_ALL_LABEL,
  FILTER_COUNT_BADGE_SIZE,
  FILTER_MENU_MAX_HEIGHT,
  FILTER_MENU_SEARCH_ROW_HEIGHT,
  FILTER_MENU_WIDTH,
  FILTER_OPTION_MIN_HEIGHT,
  FILTER_SELECTED_BAR_WIDTH,
  FILTER_SHEET_HEADER_MIN_HEIGHT,
  FILTER_SHEET_HEIGHT_PERCENT,
  FILTER_SHEET_OPTION_MIN_HEIGHT,
  FILTER_TRIGGER_HEIGHT,
  getFilterSearchPlaceholder,
  getFilterTriggerAriaLabel,
  getNoFilterMatchesText,
  MARKETPLACE_FILTER_DATA_ACTIONS,
  MARKETPLACE_FILTER_LABELS,
  MARKETPLACE_FILTER_NOUNS,
  MARKETPLACE_FILTER_ORDER,
  MARKETPLACE_FILTER_SEARCH_THRESHOLD,
  MARKETPLACE_MAX_COLLECTION_FILTERS,
  SHOW_ITEMS_LABEL,
} from '../../../constants/marketplace';
import { HOVER_MEDIA, PILL_BORDER_RADIUS } from '../../../constants/ui';
import { marketplaceSetFiltersAction } from '../../../store/actions/marketplace';
import { selectMarketplaceFilters, selectMarketplaceMetadata } from '../../../store/selectors/marketplace';
import { IMarketplaceFilterOption, MarketplaceFilterKindEnum } from '../../../types/marketplace';
import { toggleFilterValue } from '../../../util/marketplace';

interface IProps {
  kind: MarketplaceFilterKindEnum;
}

const triggerSx: SxProps<Theme> = {
  height: FILTER_TRIGGER_HEIGHT,
  px: 1.5,
  flexShrink: 0,
  borderRadius: 1,
  color: 'text.primary',
  typography: 'body1',
  fontWeight: 400,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '& .MuiButton-endIcon': { ml: 0.25, mr: -0.5, color: 'action.active' },
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: 'action.hover' },
  },
};

// Tint only on hover or while open.
const appliedTriggerSx: SxProps<Theme> = {
  ...triggerSx,
  color: 'primary.main',
  '& .MuiButton-endIcon': { ml: 0.25, mr: -0.5, color: 'primary.main' },
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity) },
  },
};

const openTriggerSx: SxProps<Theme> = {
  ...appliedTriggerSx,
  backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity),
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.selectedOpacity) },
  },
};

const countBadgeSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: FILTER_COUNT_BADGE_SIZE,
  height: FILTER_COUNT_BADGE_SIZE,
  px: 0.625,
  ml: 0.75,
  borderRadius: PILL_BORDER_RADIUS,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1,
};

// The Menu paper is the scroll container, so the row sticks to its top instead of scrolling away.
const menuSearchRowSx: SxProps<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  px: 1.5,
  pt: 1.5,
  pb: 0.5,
  backgroundColor: 'background.paper',
};

const sheetSearchRowSx: SxProps<Theme> = { flexShrink: 0, mx: 2, mb: 1 };

const optionSx: SxProps<Theme> = {
  minHeight: FILTER_OPTION_MIN_HEIGHT,
  py: 0.25,
  pl: 0.75,
  pr: 2,
  gap: 0.5,
  borderLeft: FILTER_SELECTED_BAR_WIDTH,
  borderLeftStyle: 'solid',
  borderLeftColor: 'transparent',
  whiteSpace: 'normal',
};

const selectedOptionSx: SxProps<Theme> = {
  ...optionSx,
  borderLeftColor: 'primary.main',
  backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.selectedOpacity),
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity) },
  },
};

const sheetOptionSx: SxProps<Theme> = { ...optionSx, minHeight: FILTER_SHEET_OPTION_MIN_HEIGHT, py: 0.625 };

const sheetSelectedOptionSx: SxProps<Theme> = {
  ...selectedOptionSx,
  minHeight: FILTER_SHEET_OPTION_MIN_HEIGHT,
  py: 0.625,
};

const checkboxSx: SxProps<Theme> = { p: 0.75 };

const noMatchSx: SxProps<Theme> = { px: 2, py: 1.5 };

const sheetBodySx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' };

const sheetHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  minHeight: FILTER_SHEET_HEADER_MIN_HEIGHT,
  px: 2,
  pb: 1.5,
};

const sheetListSx: SxProps<Theme> = { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', py: 0, pb: 1 };

const sheetFooterSx: SxProps<Theme> = {
  flexShrink: 0,
  borderTop: 1,
  borderColor: 'divider',
  px: 2,
  pt: 1.5,
  pb: ({ spacing }) => `calc(${spacing(1.5)} + env(safe-area-inset-bottom, 0px))`,
};

const searchStartAdornment = (
  <InputAdornment position="start">
    <SearchIcon sx={{ fontSize: 20, color: 'action.active' }} />
  </InputAdornment>
);

// Keeps list type-ahead out of the field; Escape and Tab must still reach the menu. The field sits outside
// MenuList's own arrow navigation, so ArrowDown hands focus to the first option by hand.
const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key === 'Escape' || event.key === 'Tab') {
    return;
  }

  event.stopPropagation();

  if (event.key === 'ArrowDown') {
    const firstOption = event.currentTarget.parentElement?.querySelector<HTMLElement>(
      '[role="menuitemcheckbox"]:not([aria-disabled="true"])',
    );

    if (firstOption) {
      event.preventDefault();
      firstOption.focus();
    }
  }
};

const matchIndex = (option: IMarketplaceFilterOption, query: string): number =>
  option.label.toLowerCase().indexOf(query.toLowerCase());

const matchesQuery = (option: IMarketplaceFilterOption, query: string): boolean => matchIndex(option, query) >= 0;

const isDefined = (option?: IMarketplaceFilterOption): option is IMarketplaceFilterOption => !!option;

export const MarketplaceFilterMenu = ({ kind }: IProps) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const filters = useSelector(selectMarketplaceFilters);
  const metadata = useSelector(selectMarketplaceMetadata);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Snapshot at open time, so new picks don't jump to the top mid-session.
  const [pinned, setPinned] = useState<string[]>([]);

  const options = metadata[kind];
  const selected = filters[kind];
  const label = MARKETPLACE_FILTER_LABELS[kind];
  const dataAction = MARKETPLACE_FILTER_DATA_ACTIONS[kind];
  const noun = MARKETPLACE_FILTER_NOUNS[kind];
  const menuId = `marketplace-filter-${kind}`;
  const trimmedQuery = query.trim();

  const isOpen = isMobileViewport ? isSheetOpen : !!anchorEl;
  const isApplied = selected.length > 0;
  const isSearchable = options.length > MARKETPLACE_FILTER_SEARCH_THRESHOLD;
  const isCollectionCapReached =
    kind === MarketplaceFilterKindEnum.Collections && selected.length >= MARKETPLACE_MAX_COLLECTION_FILTERS;
  const isPinning = isSearchable && !trimmedQuery;

  const pinnedOptions = useMemo(
    () => (isPinning ? pinned.map((value) => options.find((option) => option.value === value)).filter(isDefined) : []),
    [isPinning, pinned, options],
  );

  const visibleOptions = useMemo(() => {
    const matching = trimmedQuery ? options.filter((option) => matchesQuery(option, trimmedQuery)) : options;
    const rest = matching.filter((option) => !pinnedOptions.includes(option));

    return [...pinnedOptions, ...rest];
  }, [options, trimmedQuery, pinnedOptions]);

  const currentTriggerSx = useMemo(() => {
    if (isOpen) {
      return openTriggerSx;
    }

    return isApplied ? appliedTriggerSx : triggerSx;
  }, [isOpen, isApplied]);

  const triggerAriaLabel = useMemo(
    () => (isApplied ? getFilterTriggerAriaLabel(label, selected.length) : undefined),
    [isApplied, label, selected.length],
  );

  const noMatchText = useMemo(() => getNoFilterMatchesText(noun, trimmedQuery), [noun, trimmedQuery]);

  const menuSlotProps = useMemo(
    () => ({
      paper: {
        sx: {
          width: FILTER_MENU_WIDTH,
          maxWidth: ({ spacing }: Theme) => `calc(100% - ${spacing(2)})`,
          maxHeight: FILTER_MENU_MAX_HEIGHT + (isSearchable ? FILTER_MENU_SEARCH_ROW_HEIGHT : 0),
          mt: 0.5,
        },
      },
    }),
    [isSearchable],
  );

  const sheetSx: SxProps<Theme> = useMemo(
    () => ({
      '& .MuiDrawer-paper': {
        px: 0,
        overflow: 'hidden',
        ...(isSearchable && { height: `${FILTER_SHEET_HEIGHT_PERCENT}%` }),
      },
    }),
    [isSearchable],
  );

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setPinned(isSearchable ? selected : []);

      if (isMobileViewport) {
        setIsSheetOpen(true);
      } else {
        setAnchorEl(event.currentTarget);
      }
    },
    [isSearchable, selected, isMobileViewport],
  );

  const handleSheetOpen = useCallback(() => setIsSheetOpen(true), []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setIsSheetOpen(false);
    setQuery('');
  }, []);

  // A menu left open across the breakpoint would reopen by itself on the way back.
  useEffect(() => {
    handleClose();
  }, [isMobileViewport, handleClose]);

  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value),
    [],
  );

  const handleQueryClear = useCallback(() => setQuery(''), []);

  const handleToggle = useCallback(
    (value: string) => dispatch(marketplaceSetFiltersAction({ [kind]: toggleFilterValue(selected, value) })),
    [dispatch, kind, selected],
  );

  const handleDeselectAll = useCallback(() => dispatch(marketplaceSetFiltersAction({ [kind]: [] })), [dispatch, kind]);

  const renderOptionLabel = useCallback(
    (option: IMarketplaceFilterOption) => {
      const at = trimmedQuery ? matchIndex(option, trimmedQuery) : -1;

      if (at < 0) {
        return option.label;
      }

      return (
        <>
          {option.label.slice(0, at)}
          <Box component="span" fontWeight="medium">
            {option.label.slice(at, at + trimmedQuery.length)}
          </Box>
          {option.label.slice(at + trimmedQuery.length)}
        </>
      );
    },
    [trimmedQuery],
  );

  const renderOption = useCallback(
    (option: IMarketplaceFilterOption, index: number) => {
      const isSelected = selected.includes(option.value);
      const isDisabled = isCollectionCapReached && !isSelected;
      const isAfterPinned = pinnedOptions.length > 0 && index === pinnedOptions.length;

      let sx = isSelected ? selectedOptionSx : optionSx;

      if (isMobileViewport) {
        sx = isSelected ? sheetSelectedOptionSx : sheetOptionSx;
      }

      const item = (
        <MenuItem
          key={option.value}
          role="menuitemcheckbox"
          aria-checked={isSelected}
          disabled={isDisabled}
          onClick={() => handleToggle(option.value)}
          data-action={`${dataAction}_option`}
          sx={sx}
        >
          <Checkbox checked={isSelected} size="small" tabIndex={-1} disableRipple sx={checkboxSx} />
          <ListItemText primary={renderOptionLabel(option)} />
        </MenuItem>
      );

      // MenuList injects focus props into its first child, so options must be plain siblings, not Fragments.
      return isAfterPinned ? [<Divider key={`${option.value}_divider`} sx={{ my: 1 }} />, item] : item;
    },
    [
      selected,
      isCollectionCapReached,
      pinnedOptions.length,
      isMobileViewport,
      handleToggle,
      dataAction,
      renderOptionLabel,
    ],
  );

  const searchSlotProps = useMemo(
    () => ({
      htmlInput: { 'data-action': `${dataAction}_search` },
      input: {
        startAdornment: searchStartAdornment,
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={handleQueryClear}
              data-action={`${dataAction}_search_clear`}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      },
    }),
    [query, handleQueryClear, dataAction],
  );

  if (!options.length) {
    return null;
  }

  const searchField = (
    <TextField
      value={query}
      onChange={handleQueryChange}
      placeholder={getFilterSearchPlaceholder(noun)}
      aria-label={getFilterSearchPlaceholder(noun)}
      size="small"
      fullWidth
      autoFocus
      slotProps={searchSlotProps}
    />
  );

  const list = visibleOptions.length ? (
    visibleOptions.flatMap(renderOption)
  ) : (
    <Typography variant="body2" color="text.secondary" sx={noMatchSx}>
      {noMatchText}
    </Typography>
  );

  return (
    <>
      <Button
        onClick={handleOpen}
        endIcon={isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        aria-haspopup={isMobileViewport ? 'dialog' : 'menu'}
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        aria-label={triggerAriaLabel}
        data-action={dataAction}
        sx={currentTriggerSx}
      >
        {label}
        {isApplied && (
          <Box component="span" aria-hidden sx={countBadgeSx}>
            {selected.length}
          </Box>
        )}
      </Button>
      {isMobileViewport ? (
        <BottomSheet
          open={isSheetOpen}
          onClose={handleClose}
          onOpen={handleSheetOpen}
          maxHeightPercent={FILTER_SHEET_HEIGHT_PERCENT}
          sx={sheetSx}
        >
          <Box id={menuId} role="dialog" aria-label={label} sx={sheetBodySx}>
            <Box sx={sheetHeaderSx}>
              <Typography variant="h6" fontWeight="medium">
                {label}
              </Typography>
              {isApplied && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleDeselectAll}
                  data-action={`${dataAction}_deselect_all`}
                >
                  {DESELECT_ALL_LABEL}
                </Button>
              )}
            </Box>
            {isSearchable && (
              <Box sx={sheetSearchRowSx} onKeyDown={handleSearchKeyDown}>
                {searchField}
              </Box>
            )}
            <MenuList sx={sheetListSx}>{list}</MenuList>
            <Box sx={sheetFooterSx}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleClose}
                data-action={`${dataAction}_show_items`}
              >
                {SHOW_ITEMS_LABEL}
              </Button>
            </Box>
          </Box>
        </BottomSheet>
      ) : (
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleClose}
          // The search field owns focus; otherwise the first option takes it.
          autoFocus={!isSearchable}
          slotProps={menuSlotProps}
        >
          {isSearchable && (
            <Box sx={menuSearchRowSx} onKeyDown={handleSearchKeyDown}>
              {searchField}
            </Box>
          )}
          {list}
        </Menu>
      )}
    </>
  );
};

const menusRowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 };

export const MarketplaceFilterMenus = () => (
  <Box sx={menusRowSx}>
    {MARKETPLACE_FILTER_ORDER.map((kind) => (
      <MarketplaceFilterMenu key={kind} kind={kind} />
    ))}
  </Box>
);
